import * as React from "react";
import { styled, css } from "styled-components";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { useLinkProps } from "../../hooks/useLinkProps.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";
import { surfaceColorMap } from "../../util/styles.js";
import type { BackgroundColor } from "../../types/theme.js";

export interface CardGridProps {
  title?: string;
  cards?: Partial<CardProps>[];
  backgroundColor?: BackgroundColor;
}

interface CardProps {
  image?: Image;
  link: Link;
}

export const ServiceGrid: React.FC<CardGridProps> = ({ title, cards = [], backgroundColor }) => {
  return (
    <VerticalSpacer>
      <Constrainer>
        <HorizontalSpacer>
          <ServiceGridTitle {...createEditableProps("text", "title")}>{title}</ServiceGridTitle>
          <ServiceGridList>
            {cards
              .filter((x) => x.link)
              .map((card, index) => (
                <Card key={index} card={card as CardProps} backgroundColor={backgroundColor} />
              ))}
          </ServiceGridList>
        </HorizontalSpacer>
      </Constrainer>
    </VerticalSpacer>
  );
};

interface CardItemProps {
  card: CardProps;
  backgroundColor?: BackgroundColor;
}

const Card: React.FC<CardItemProps> = ({ card, backgroundColor }) => {
  const linkProps = useLinkProps(card.link!);

  return (
    <ServiceGridCard {...linkProps} $bg={backgroundColor}>
      <ServiceGridCardImage>
        {card.image?.url && (
          <img src={card.image.url} alt={card.image.altText || ""} />
        )}
      </ServiceGridCardImage>
      <h3>{card.link!.title}</h3>
    </ServiceGridCard>
  );
};


const ServiceGridTitle = styled.h2`
  color: var(--servicegrid-title-color, inherit);
`;

const ServiceGridList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing.s};
  margin-top: ${(props) => props.theme.spacing.s};

  @media (min-width: 960px) {
    grid-template-columns: 1fr 1fr;
    margin-top: ${(props) => props.theme.spacing.m};
  }
`;

const ServiceGridCard = styled.a<{ $bg?: BackgroundColor }>`
  ${(p) =>
    p.$bg
      ? css`
          background-color: ${surfaceColorMap[p.$bg]};
          color: ${p.$bg === "surface3" ? "#fff" : "var(--swatch2)"};
          svg path { fill: currentColor; stroke: currentColor; }
          h3 { color: inherit; }
        `
      : css`
          background-color: var(--servicegrid-card-background);
          color: var(--swatch2);
        `}
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.s};
  border-radius: ${(props) => props.theme.borderRadius};
  padding: ${(props) => props.theme.spacing.s};
  text-decoration: none;

  h3 {
    margin: 0;
    font-weight: 400;
  }
`;

const ServiceGridCardImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 50px;
  height: 50px;

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;
